import { useQueryClient } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { useConversionContext } from '../../../contexts/ConversionProvider';
import useConversionActions from '../../../hooks/APIHooks/appointments/useConversionActions';
import { Conversion } from '../../../types/conversion';
import { fetchErrorMessage } from '../../../utils/fetchErrorMessage';
import ConsentModal from '../../CommonComps/Modals/ConsentModal';
import CreateEditConversionModal from '../../CommonComps/Modals/CreateEditConversionModal';

const ConversionModalContainer = () => {
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  // integration of context hooks here
  const {
    targetedConversion,
    showDeleteModal,
    showEditModal,
    setTargetedConversion,
    setShowDeleteModal,
    setShowEditModal,
  } = useConversionContext();

  // integration of API hooks here
  const { deleteConversionByIdHandler } = useConversionActions('');

  // integration of tanstack query hooks here
  const queryClient = useQueryClient();

  // handler function to delete conversion declared here
  const deleteConversionHandler = async () => {
    try {
      setIsDeleting(true);

      await deleteConversionByIdHandler
        .mutateAsync({ conversionId: targetedConversion?.id as number })
        .then((res) => {
          if (res.status === 204) {
            toast.success('Conversion Deleted!!');

            setShowDeleteModal(false);
            setTargetedConversion(null);

            queryClient.invalidateQueries({ queryKey: ['conversion-list'] });
          } else {
            toast.error(res.data.message || 'Conversion Deleting Failed!!');
          }
        })
        .catch((error) => toast.error(fetchErrorMessage(error)));
    } catch (error) {
      toast.error('Something Went Wrong!!');
    } finally {
      setIsDeleting(false);
    }
  };

  // rendering conversion modal container component here
  return (
    <section>
      <AnimatePresence initial={false} mode='wait' onExitComplete={() => null}>
        {showEditModal ? (
          <CreateEditConversionModal
            title='update'
            handleModalClose={() => setShowEditModal(false)}
            conversion={targetedConversion as Conversion}
          />
        ) : null}
      </AnimatePresence>

      <AnimatePresence initial={false} mode='wait' onExitComplete={() => null}>
        {showDeleteModal ? (
          <ConsentModal
            titleText='Are you sure?'
            subTitle={
              <>
                Are you sure you want to delete this{' '}
                <span className='font-semibold text-base-light-blue'>
                  ({targetedConversion?.name})
                </span>{' '}
                conversion? Once you delete it, you can not recover it.
              </>
            }
            isLoading={isDeleting}
            yesBtnTxt='Yes'
            yesBtnVariant='danger'
            noBtnTxt='No'
            noBtnVariant='success'
            mutationHandler={deleteConversionHandler}
            handleModalClose={() => setShowDeleteModal(false)}
          />
        ) : null}
      </AnimatePresence>
    </section>
  );
};

export default ConversionModalContainer;
